import {
  ConfigMetric,
  ConfigTabId,
  ScopedNumbersConfig,
} from './configTypes';
import { pluralize } from './configUtils';

export const CONFIG_TABS: { key: ConfigTabId; label: string }[] = [
  { key: 'ramp', label: 'Onboarding Ramp' },
  { key: 'cadence', label: 'Cadence' },
  { key: 'metrics', label: 'Metrics' },
  { key: 'focus', label: 'Focus Areas' },
  { key: 'escalation', label: 'Escalation' },
  { key: 'history', label: 'Change History' },
];

/** Page header subtitle for each tab (changes with the active tab). */
export const TAB_SUBTITLES: Record<ConfigTabId, string> = {
  ramp: 'Onboarding duration, milestone weeks, and weekly Pulse Check targets for each org and department, with optional location overrides',
  cadence: 'Pulse check and observation windows for each org and department, with optional location overrides',
  metrics: 'Metrics, national targets, location overrides, and the mobile roster card for each org and department',
  focus: 'Process focus areas for coaching that does not come from an observation behavior, for each org and department',
  escalation: 'When pulse check results escalate to a Full observation, and when a coaching item closes, for each org and department',
  history: 'Every configuration change, who made it, and what it was before',
};

/** The fixed metric list. Every org and department starts with all five. */
export const CONFIG_METRICS: ConfigMetric[] = [
  { id: 'DPS', name: 'DPS', direction: 'higher', unit: '%', source: 'dps_pct' },
  { id: 'KU', name: 'KU', direction: 'higher', unit: '%', source: 'ku_pct' },
  { id: 'Gap Time', name: 'Gap Time', direction: 'lower', unit: 'min', source: 'gap_time_min' },
  { id: 'BPA', name: 'BPA', direction: 'higher', unit: 'boxes/hr', source: 'bpa' },
  { id: 'Cases Picked', name: 'Cases Picked', direction: 'higher', unit: 'cases/day', source: 'cases_picked' },
];

/** Viewport width under which the page is replaced by the desktop-only message. */
export const DESKTOP_MIN_WIDTH = 768;

export const DESKTOP_ONLY_MESSAGE = 'People configuration is available on desktop.';
export const LOAD_ERROR_MESSAGE = 'Configuration could not be loaded.';

// Optimistic lock message (dummy name/time until the record version comes from the API).
export const CONFLICT_MESSAGE =
  'This setting was changed by K. Brennan at 9:42 AM. Reload to see the current value.';

export const CADENCE_CONFIG: ScopedNumbersConfig = {
  section: 'Cadence',
  sectionKey: 'cadence',
  nationalTitle: 'National Cadence',
  nationalSub: (org, dept) =>
    `How often each ${org} ${dept} employee is owed a pulse check and an observation, at every location.`,
  colHead: ['Activity', 'Window'],
  editLabel: 'Edit Cadence',
  saveLabel: 'Save Cadence',
  noun: 'cadence',
  effective: 'Next window boundary',
  footnote:
    'A change takes effect at the next window boundary. Windows already open keep their due dates.',
  value: (f, v) => `Every ${pluralize(v, f.unit)}`,
  confirm: {
    title: 'Save cadence changes',
    body: 'Changing the window length will regenerate future assignment windows. Assignments already closed as Missed are not affected.',
  },
  fields: [
    {
      key: 'pulseWeeks',
      row: 'Pulse Checks',
      label: 'Pulse Check window',
      unit: 'fiscal week',
      hint: 'Default 2. Each window starts on a fiscal week boundary.',
    },
    {
      key: 'obsPeriods',
      row: 'Observations',
      label: 'Observation window',
      unit: 'fiscal period',
      hint: 'Default 1. Each window starts on a fiscal period boundary.',
    },
  ],
};

export const ESCALATION_CONFIG: ScopedNumbersConfig = {
  section: 'Escalation',
  sectionKey: 'escalation',
  nationalTitle: 'National Escalation',
  nationalSub: (org, dept) =>
    `Thresholds for every ${org} ${dept} employee, at every location. Each counts consecutive No Improvement Shown results.`,
  colHead: ['Rule', 'Threshold'],
  editLabel: 'Edit Escalation',
  saveLabel: 'Save Escalation',
  noun: 'thresholds',
  effective: 'Next generation run',
  footnote: 'Changes apply to the next generation run. Tasks already escalated are not changed.',
  value: (f, v) => `After ${pluralize(v, f.unit)}`,
  confirm: null,
  fields: [
    {
      key: 'escalate',
      row: 'Pulse Check escalation',
      label: 'Consecutive No Improvement Shown results',
      unit: 'result',
      hint: 'When an employee reaches this count, their next observation must be a Full observation. Default 2.',
    },
    {
      key: 'forceClose',
      row: 'Coaching forced close',
      label: 'Consecutive No Improvement Shown reviews on one item',
      unit: 'review',
      hint: 'The item closes as Completed and its owner is notified. Default 3.',
    },
  ],
};
